"use client";

import { useLanguage } from "@/contexts/LanguageContext";

export default function SectionWorkflows() {
  const { t } = useLanguage();

  return (
    <section
      id="workflows"
      className="bg-surface py-32 px-6 md:px-12 text-text-primary relative"
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <span className="inline-block py-1 px-3 rounded-full bg-brand-primary/10 text-brand-primary text-sm font-mono tracking-wider mb-4 border border-brand-primary/20">
              {t("sectionWorkflows.badge")}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 leading-tight">
              {t("sectionWorkflows.title.before")} <br />
              <span className="text-text-secondary">
                {t("sectionWorkflows.title.middle")}{" "}
              </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-primary to-brand-secondary font-bold">
                {t("sectionWorkflows.title.highlight")}
              </span>
              <span className="text-text-secondary">
                {" "}
                {t("sectionWorkflows.title.after")}
              </span>
            </h2>
            <p className="text-text-secondary text-lg leading-relaxed mb-8">
              {t("sectionWorkflows.subtitle")}
            </p>

            <div className="flex gap-4">
              <div className="h-1 w-20 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-full" />
            </div>
          </div>

          <div className="space-y-6">
            <div className="group p-6 rounded-2xl bg-surface-card border border-border-default hover:border-border-hover transition-colors">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-brand-secondary/10 text-brand-secondary">
                  <svg
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6"
                    role="img"
                    aria-labelledby="icon-devex"
                  >
                    <title id="icon-devex">
                      {t("sectionWorkflows.devex.iconTitle")}
                    </title>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-text-primary mb-2 group-hover:text-brand-secondary transition-colors">
                    {t("sectionWorkflows.devex.title")}
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    {t("sectionWorkflows.devex.desc")}
                  </p>
                </div>
              </div>
            </div>

            <div className="group p-6 rounded-2xl bg-surface-card border border-border-default hover:border-border-hover transition-colors">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-brand-primary/10 text-brand-primary">
                  <svg
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6"
                    role="img"
                    aria-labelledby="icon-automation"
                  >
                    <title id="icon-automation">
                      {t("sectionWorkflows.automation.iconTitle")}
                    </title>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-text-primary mb-2 group-hover:text-brand-primary transition-colors">
                    {t("sectionWorkflows.automation.title")}
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    {t("sectionWorkflows.automation.desc")}
                  </p>
                </div>
              </div>
            </div>

            <div className="group p-6 rounded-2xl bg-surface-card border border-border-default hover:border-border-hover transition-colors">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-brand-primary/10 text-brand-primary">
                  <svg
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6"
                    role="img"
                    aria-labelledby="icon-deploy"
                  >
                    <title id="icon-deploy">
                      {t("sectionWorkflows.deploys.iconTitle")}
                    </title>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-text-primary mb-2 group-hover:text-brand-primary transition-colors">
                    {t("sectionWorkflows.deploys.title")}
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    {t("sectionWorkflows.deploys.desc")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
